import React, { Fragment, useEffect, useState } from 'react';
import SplitReportPage from './SplitReportPage';
import usePrevious from 'helpers/hooks/usePrevious';
import {
  SPLIT_REPORTS,
  SPLIT_REPORTS_DELETE,
  SPLIT_REPORTS_REQUESTS,
  SPLIT_REPORTS_REQUEST_UPDATE,
} from 'constants/apis';
import { buildUrl } from 'helpers/misc';
import { ConfirmDelete, NoInformationFound } from 'Components/index';
import { Fetch } from 'helpers/index';
import { copy as deepCopy } from 'helpers/deep';
import i18n from 'src/i18n';
import { useHistory } from 'react-router-dom';
import DownloadFromUrl from 'helpers/download';
import InvoicesListMobile from 'src/containers/Invoices/InvoicesList/InvoicesListMobile/InvoicesListMobile';

const SplitReportTab = (props) => {
  const history = useHistory();
  //#region : Variables Section:
  const { appState, setAppState, deviceIndex = 1, pageSize, IsInternalUser, clientNumber } = props;

  //! State:
  const [pageState, setPageState] = useState({
    data: [],
    subReports: [],
    menuRow: null,
    hasMixedCurrency: false,
    isLoading: false,
    shouldLoad: true,
    totalItems: 0,
    pageNumber: 1,
    sort: '-RequestId',
    code: 'splitReport',
    viewCode: 'splitReport',
    hasMixedCurrency: false,
    isRowLoading: false,
    showDeleteConfirmation: false,
    itemToDeleteTableIndex: null,
  });

  const prevPageSize = usePrevious(pageSize);
  //Note: Example of sort options: you may set sort to column name prefixed by - or + as follows: {sort: '-RequestNumber',} or {sort: '+RequestNumber',}

  //#endregion : Variables Section: END

  //#region : Functions Section:

  //! Delete Confirmation Setter:
  const setShowDeleteConfirmation = (value) => {
    setPageState((prev) => ({ ...prev, showDeleteConfirmation: value }));
  };

  //! Sorting Handler:
  const handleSortClick = (sort) => {
    const abortController = new AbortController();
    setPageState((prev) => ({ ...prev, isLoading: true, sort }));
    fetchSplitReportsRequests(abortController, sort);
  };

  //! Split Reports Fetch Function:
  const fetchSplitReportsRequests = async (newAbortController, sort = null) => {
    setPageState((prev) => ({ ...prev, data: [] }));

    const URL = buildUrl(SPLIT_REPORTS_REQUESTS, {
      ClientNumber: IsInternalUser ? clientNumber : undefined,
      pageSize: pageSize,
      pageNumber: pageState.pageNumber - 1,
      sort: sort || pageState.sort,
    });

    try {
      const response = await Fetch.Get(URL, {
        signal: newAbortController.signal,
        isGlobalLoader: false,
      });

      let data = await response.json();

      const splitReports = data.DataList.map((item) => {
        item.Expanded = false;
        item.fetched = false;
        return item;
      });

      setPageState((prev) => {
        return {
          ...prev,
          data: [...splitReports] || [],
          totalItems: data.MatchCriteriaCount,
          isLoading: false,
          shouldLoad: false,
        };
      });
    } catch (err) {
      setPageState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      setAppState(() => {
        return { isGlobalLoaderShowing: false };
      });
      console.error(err);
    }
  };

  //! TODO: REMOVE THIS FUNCTION:
  const undoDelete = () => {
    const URL = SPLIT_REPORTS_REQUEST_UPDATE;
    for (let report of pageState.data) {
      let updatedItem = {};

      if (pageState.data.indexOf(report) < 6) {
        const RequestId = report.RequestNumber.slice(1);
        updatedItem = {
          ...report,
          RequestId,
          Status: 'C',
          StatusName: 'Completed',
          submittedBy: 'Max',
        };
      } else if (pageState.data.indexOf(report) < 11) {
        const RequestId = report.RequestNumber.slice(1);
        updatedItem = {
          ...report,
          RequestId,
          Status: 'D',
          StatusName: 'Draft',
          submittedBy: 'Max',
        };
      } else {
        const RequestId = report.RequestNumber.slice(1);
        updatedItem = {
          ...report,
          RequestId,
          Status: 'S',
          StatusName: 'Submitted',
          submittedBy: 'Max',
        };
      }

      Fetch.Put(URL, updatedItem)
        .then((response) => response.json())
        .then((data) => {
          setPageState((prev) => ({
            ...prev,
            data: prev.data.filter((row) => row.Status !== 'R' || []),
            isLoading: true,
          }));
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setPageState((prev) => ({
            ...prev,
            isLoading: true,
          }));
          setAppState(() => {
            return { isGlobalLoaderShowing: false };
          });
        });
    }
  };

  // undoDelete();

  //! Fetch and update the subRows array in the state if the subRow was not fetched before:
  const fetchSubRows = (row) => {
    const { RequestId } = row;

    setPageState((previousState) => ({ ...previousState, isRowLoading: true }));

    const url = buildUrl(`${SPLIT_REPORTS}/${RequestId}/reports`);

    Fetch.Get(url)
      .then((response) => response.json())
      .then((data) => {
        const originalData = deepCopy(pageState.data);
        const row = originalData.find((item) => item.RequestId === RequestId);
        Object.assign(row, { SplitReports: [...data.DataList], fetched: true, Expanded: true });

        setPageState((previousState) => ({
          ...previousState,
          data: originalData,
          isRowLoading: false,
        }));
      })
      .catch((error) => {
        //TODO: MAX - Replace with an error message popover.
        setPageState((prev) => ({
          ...prev,
          isLoading: true,
        }));
        setAppState(() => {
          return { isGlobalLoaderShowing: false };
        });
        console.error(error);
      });
  };

  //! Delete Split Report Request Function:
  const deleteSplitReportRequest = (tableIndex) => {
    const abortController = new AbortController();

    if (tableIndex === null || tableIndex === undefined) return;

    const item = pageState.data[tableIndex];
    const URL = SPLIT_REPORTS_DELETE;
    const RequestId = item.RequestNumber.slice(1);

    Fetch.Delete(`${URL}/${RequestId}`).then((_response) => {
      fetchSplitReportsRequests(abortController);
      setPageState((prev) => ({ ...prev, isLoading: true }));
    });

    setShowDeleteConfirmation(false);
  };

  //! Temporary function to update a Split Report Request record to a specific data for testing purposes(Triggered on a click on a row):
  const tempUpdateSplitReportRequestData = async (tableIndex) => {
    const item = pageState.data[tableIndex];

    if (item.Status === 'C' && item.StatusName === 'Completed') {
      const RequestId = item.RequestNumber.slice(1);
      delete item.fetched;
      delete item.Expanded;
      // const RequestId = item.RequestNumber.slice(1);

      const URL = buildUrl(SPLIT_REPORTS_REQUEST_UPDATE);

      const body = {
        ...item,
        RequestId,
        Status: 'D',
        StatusName: 'Draft',
        SubmittedBy: 'Max',
      };

      const response = await Fetch.Put(URL, { ...body });
      const data = await response.json();

      if (response && response.ok) {
        console.log({ RESPONSE: data });
        setPageState((prev) => ({ ...prev, isLoading: true }));
      }
    } else {
      // alert('Cannot Be Changed');
    }
  };

  //! Row menu options function:
  const onOptionClick = (code, tableIndex, recordData = {}) => {
    const { RequestNumber, JobNumber } = recordData;
    const requestId = RequestNumber.slice(1);

    switch (code) {
      //* EDIT:
      case 'edit': {
        //! Move teh user to /split-report/edit url:
        history.push(`/split-report/edit/${RequestNumber}`);
        setAppState({
          SplitReportEditInfo: {
            ReportRecordData: recordData,
            jobNumber: recordData.JobNumber,
          },
        });
        break;
      }

      //* DELETE:
      case 'delete': {
        setPageState((pre) => ({ ...pre, showDeleteConfirmation: true, itemToDeleteTableIndex: tableIndex }));
        break;
      }

      //* DOWNLOAD ALL REPORTS:

      case 'download': {
        let url = `${SPLIT_REPORTS}/${requestId}/downloadall`;

        if (IsInternalUser) {
          url = buildUrl(url, { JobNumber, clientNumber });
          console.log({ url });
        }

        DownloadFromUrl(url).catch((error) => console.log({ error: error.message }));
        break;
      }

      default: {
        break;
      }
    }

    setPageState((pre) => ({ ...pre, menuRow: null }));
  };

  //! Page number change handler:
  const handlePageNumberChange = (pageNumber) => {
    setPageState((pre) => ({
      ...pre,
      isLoading: true,
      pageNumber: Number(pageNumber),
    }));
  };

  //! Check if the page is not loading and there is no data to display
  //! Returns No Information Found if the condition is true to short circuit the rendering:
  const isLoadingCheck = () => {
    if (!pageState.isLoading && pageState.data.length === 0) {
      return <NoInformationFound titleText={i18n.t('splitReports:NoSplitReportRequestsToShow')} />;
    }
  };

  //! Row expansion handler:
  const handleTrExpand = (e, index) => {
    e.stopPropagation();
    //? Activate this line and comment the rest of the function's code to temporary change the row expansion functionality to update the data of a row on expansion button click, with the data specified in the body variable inside the function "tempUpdateSplitReportRequestData":
    // tempUpdateSplitReportRequestData(index);

    const item = pageState.data[index];
    const dataCopy = [...deepCopy(pageState.data)];
    dataCopy[index].Expanded = !item.Expanded;

    setPageState((pre) => ({ ...pre, data: dataCopy }));

    //! If Sub row has been fetched previously, then just update the data in the state and return:
    if (!item.fetched) {
      //! If Sub row has not been fetched previously, then fetch it and update the data in the state:
      fetchSubRows(item);
    }
  };

  //! Row menu options display handler:
  const handleMoreVertClick = (e, index) => {
    if ((e.target.tagName === 'SPAN' || e.target.tagName === 'I') && e.target.textContent !== 'more_vert') return;
    const val = pageState.menuRow === index ? null : index;

    setPageState((pre) => ({ ...pre, menuRow: val }));
  };

  // ! Follow this for downloading a report:
  const handleDownload = (e, reportId) => {
    e.preventDefault();
    const url = buildUrl(`${SPLIT_REPORTS}/${reportId}/download`, { clientNumber });
    DownloadFromUrl(url);
  };
  //#endregion : Functions Section: END

  //#region : component First load and reload section:
  // ! 1: Handling the page size change to trigger reloading the page to refetch the new records for the page size specified that will be sent with the request. The isLoading === true during the component remount or mount phase will trigger the fetchSplitReportsRequests function to fetch the data.
  useEffect(() => {
    if (prevPageSize !== pageSize) {
      setPageState((prev) => ({ ...prev, isLoading: true }));
    }
  }, [prevPageSize, pageSize]);

  //! 2: Fetching the data for the first time and if any of the dependencies change (pageSize, pageState.pageNumber, pageState.isLoading, IsInternalUser, clientNumber):
  useEffect(() => {
    const abortController = new AbortController();

    if (pageState.isLoading) {
      fetchSplitReportsRequests(abortController);
    }

    return () => {
      abortController.abort();
    };
  }, [pageSize, pageState.pageNumber, pageState.isLoading, IsInternalUser, clientNumber, pageState.shouldLoad]);

  //! 3: Handles the page number change - Similar to the handling of the page size change. this will trigger fetch splitReportsRequests function to fetch the data for the new page number:
  // useEffect(() => {
  //   const abortController = new AbortController();

  //   if (pageState.shouldLoad) {
  //     fetchSplitReportsRequests(abortController);
  //   }
  // }, [pageState.pageNumber]);

  //! 4: handles the change of isLoading state to trigger the fetchSplitReportsRequests function to fetch the data:
  // useEffect(() => {
  //   const abortController = new AbortController();
  //   if (pageState.shouldLoad) {
  //     fetchSplitReportsRequests(abortController);
  //   }
  // }, [pageState.shouldLoad]);

  //! 5: During rendering phase, calling `isLoadingCheck` function to check if the page is not loading and there is no data to display. If the condition is true, then No Information Found will be returned to short circuit the rendering:
  isLoadingCheck();

  //#endregion : component First load and reload section:

  //#region : Elements to be passed to the child components:
  const shared = {
    ...props,
    ...pageState,
    setPageState,
    handleTrExpand,
    handleMoreVertClick,
    handleMenuClick: onOptionClick,
    handleDownload,
    isLoadingCheck,
    isRowLoading: pageState.isRowLoading,
    fetchSplitReportsRequests,
    fetchSubRows,
    deleteSplitReportRequest,
    onOptionClick,
    handlePageNumberChange,
    fetchSplitReportsRequests,
    tempUpdateSplitReportRequestData,
    handleSortClick,
  };
  //#endregion : Elements to be passed to the child components:

  return (
    <Fragment>
      {deviceIndex === 0 && (
        // ! NOT FULLY IMPLEMENTED
        <SplitReportMobileView {...shared} />
      )}

      {deviceIndex === 1 && (
        <SplitReportDesktopView
          // ! Props, States, State Setters, and Functions:
          {...shared}
        />
      )}

      {pageState.showDeleteConfirmation && (
        <ConfirmDelete
          title={i18n.t('splitReports:deleteReportConfirmationMessage1')}
          onCancelDelete={() => {
            setShowDeleteConfirmation(false);
          }}
          onProceedDelete={() => {
            deleteSplitReportRequest(pageState.itemToDeleteTableIndex);
          }}
        />
      )}
    </Fragment>
  );
};

export default SplitReportTab;

//! mobile invoices table view:
function SplitReportMobileView(props) {
  const {
    pageState,
    data,
    showNoInformationFound,
    handleExportInvoices,
    hasAccessToInvoices,
    handleMoreVertClick,
    handleMenuClick,
    handleTrExpand,
    deviceIndex,
  } = props;

  //! mobile invoices table view:
  if (deviceIndex === 0) {
    //! This is a placeholder for future implementation if needed a mobile version of the split Report table.
    return (
      <InvoicesListMobile
        {...data}
        onMoreVertClick={handleMoreVertClick}
        onMenuClick={handleMenuClick}
        handleTrExpand={handleTrExpand}
        menuRow={pageState.menuRow}
        data={data}
        showNoInformationFound={showNoInformationFound}
        downloadInvoices={handleExportInvoices}
        hasAccessToInvoices={hasAccessToInvoices}
      />
    );
  }
  return null;
}

// !Desktop invoices table view:
function SplitReportDesktopView(props) {
  return (
    <Fragment>
      <SplitReportPage {...props} data={props.data} />
    </Fragment>
  );
}



// WEBPACK FOOTER //
// ./src/containers/Jobs/SplitReportTab/SplitReportTab.jsx